import { Input, Button, message } from "antd";
import { Icon } from "@terminus/dqh-web-ui";
import { get } from "lodash-es";
import { useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEY } from "@/utils";
import { getHotWords, getHotWordsOpportunities } from "@/services";
import { useKeyword, useKeywordSync } from "@/hooks";
import { useBusinessInfo } from "@/hooks";
import styles from "../search/index.module.scss";

const { Search: AntSearch } = Input;

export type SearchInputProps = {
  type?: "simple" | "normal";
  selectTab?: string;
};

export const SearchInput = (props: SearchInputProps) => {
  const { type = "normal", selectTab } = props;
  const navigate = useNavigate();
  const { keyword, setKeyword } = useKeyword();
  const location = useLocation();
  const { aiSearchStatus } = useBusinessInfo();

  // 临时代码
  const isAiPage = location.pathname.includes("/aiSearch");
  const isAi = selectTab === "business-opportunity";
  const isBusinessOpportunity = selectTab === "business-opportunity";

  useKeywordSync();

  const getHotWordsFn = useMemo(() => {
    if (selectTab === "business-opportunity") {
      return getHotWordsOpportunities;
    }
    return getHotWords;
  }, [selectTab]);

  const onSearch = useCallback(
    (v: string, type?: string) => {
      if (!v) {
        message.warning("请输入搜索关键词");
        return;
      }
      setKeyword(v);
      if (selectTab === "business-opportunity") {
        if (type === "ai") {
          if (!v) {
            message.warning("请输入要搜索的关键词");
            return;
          }
          if (aiSearchStatus === "loading") {
            message.warning("正在加载中，请稍后再试");
            return;
          }
          navigate(`/go/aiSearch?keywordAi=${v}`);
        } else {
          navigate(`/go/search?keyword=${v}`);
        }
      } else {
        if (type === "ai") {
          if (!v) {
            message.warning("请输入要搜索的关键词");
            return;
          }
          if (aiSearchStatus === "loading") {
            message.warning("正在加载中，请稍后再试");
            return;
          }
          navigate(`/s/aiSearch?keywordAi=${v}&type=keyWord`);
        } else {
          navigate(`/s/search?keyword=${v}&type=keyWord`);
        }
      }
    },
    [navigate, setKeyword, selectTab, aiSearchStatus]
  );

  const {
    data: hotWordsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [QUERY_KEY.HotWords, selectTab],
    queryFn: getHotWordsFn,
    staleTime: Infinity,
  });

  const suffix = useMemo(() => {
    return (
      <>
        <Button
          type="primary"
          onClick={() => {
            onSearch(keyword!);
          }}
          style={{
            background:
              "linear-gradient(274.04deg, #FF5600 8.49%, #FF933A 99.54%)",
            width: 86,
            border: "none",
          }}
        >
          搜索
        </Button>
        <Button
          type="primary"
          onClick={() => {
            onSearch(keyword!, "ai");
          }}
          style={{
            background:
              "linear-gradient(269.75deg, rgba(255, 86, 0, 0.26) 0.33%, rgba(255, 166, 42, 0.26) 99.9%)",
            width: 86,
            border: "none",
            color: "#FF5600",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            size={18}
            name="ai"
            style={{ marginRight: 4, alignItems: "center" }}
          />
          搜索
        </Button>
      </>
    );
  }, [onSearch, keyword]);

  const searchEle = useMemo(() => {
    return isAi || isAiPage ? (
      <AntSearch
        placeholder={
          isBusinessOpportunity
            ? "请输入商机名称、标题、内容等关键词搜索"
            : "请输入供应商名称、供货类目、产品名称等关键词搜索"
        }
        allowClear
        enterButton={<></>}
        addonAfter={null}
        size="large"
        onSearch={(v, event) => {
          onSearch(v, isAiPage ? "ai" : undefined);
        }}
        value={keyword!}
        onChange={(e) => {
          setKeyword(e.target.value);
        }}
        suffix={suffix}
        className={styles.aiSearchInput}
      />
    ) : (
      <AntSearch
        placeholder={
          isBusinessOpportunity
            ? "请输入商机名称、标题、内容等关键词搜索"
            : "请输入供应商名称、供货类目、产品名称等关键词搜索"
        }
        allowClear
        // enterButton="搜索"
        size="large"
        onSearch={(v, event) => {
          onSearch(v);
        }}
        value={keyword!}
        addonAfter={null}
        enterButton={<></>}
        className={styles.aiSearchInput}
        suffix={suffix}
        onChange={(e) => {
          setKeyword(e.target.value);
        }}
      />
    );
  }, [onSearch, keyword, isAi, isAiPage]);

  const hotWordsEle = useMemo(() => {
    // 如果还在加载中，显示加载状态
    if (isLoading) {
      return (
        <div className={styles.layoutMainSearchTags}>
          <div>加载中...</div>
        </div>
      );
    }
    // 尝试不同的数据获取路径
    let words = [];
    if (Array.isArray(hotWordsData)) {
      words = hotWordsData;
    } else if (hotWordsData && typeof hotWordsData === "object") {
      // 尝试嵌套路径
      words = get(hotWordsData, "data", []);
      if (!words || words.length === 0) {
        words = get(hotWordsData, "data.data", []);
      }
    }
    return (
      <div className={styles.layoutMainSearchTags}>
        {words && words.length > 0 ? (
          words.map(
            (
              item: { id: number; hotWord: string; searchCount: number },
              index: number
            ) => (
              <div
                style={{ display: "flex", alignItems: "center" }}
                className={styles.layoutMainActiveSearchWrapper}
                key={item.id}
              >
                {index === 0 ? (
                  <Icon
                    size={12}
                    name="Group427319869"
                    style={{ marginRight: 4 }}
                  />
                ) : null}
                <span
                  className={
                    keyword === item.hotWord
                      ? styles.layoutMainActiveSearchTag
                      : styles.layoutMainSearchTag
                  }
                  style={{ color: index === 0 ? "#FF5600" : "#1D1D1F" }}
                  onClick={() => {
                    const v = item.hotWord;
                    setKeyword(v);
                    if (selectTab === "business-opportunity") {
                      navigate(`/go/search?keyword=${v}&type=keyWord`);
                    } else {
                      navigate(`/s/search?keyword=${v}&type=keyWord`);
                    }
                  }}
                >
                  {item.hotWord}
                </span>
              </div>
            )
          )
        ) : (
          <div>暂无热搜词 (数据: {JSON.stringify(hotWordsData)})</div>
        )}
      </div>
    );
  }, [keyword, onSearch, hotWordsData, selectTab, isLoading, error]);

  return type === "simple" ? (
    <>
      {searchEle}
      {hotWordsEle}
    </>
  ) : (
    <div className={styles.layoutMainSearchContent}>
      {searchEle}
      {hotWordsEle}
    </div>
  );
};
